import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  Shield, Brain, Users, Lock, CheckCircle, ArrowRight, Play, ChevronDown, Zap,
  Eye, RotateCcw, FileText, Star, Sparkles, Target, Layers, Activity, Globe,
  Server, Code, Database, Clock, TrendingUp, Award, ChevronRight
} from 'lucide-react';
import ParticleBackground from '../components/ParticleBackground';
import AnimatedBackground from '../components/AnimatedBackground';
import GlowingCard from '../components/GlowingCard';
import TypewriterText from '../components/TypewriterText';
import InteractiveDemo from '../components/InteractiveDemo';

export default function HomePage() {
  const [activeTab, setActiveTab] = useState('overview');
  const [visibleSections, setVisibleSections] = useState({});
  const [stats, setStats] = useState({ uptime: 0, latency: 0, decisions: 0 });

  // Animated counter effect
  useEffect(() => {
    const duration = 2000;
    const steps = 60;
    const interval = duration / steps;

    let step = 0;
    const timer = setInterval(() => {
      step++;
      const progress = step / steps;
      const easeOut = 1 - Math.pow(1 - progress, 3);

      setStats({
        uptime: Math.round(99.9 * easeOut * 10) / 10,
        latency: Math.round(487 * easeOut),
        decisions: Math.round(2847563 * easeOut),
      });

      if (step >= steps) clearInterval(timer);
    }, interval);

    return () => clearInterval(timer);
  }, []);

  // Intersection observer for scroll animations
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleSections((prev) => ({
              ...prev,
              [entry.target.id]: true,
            }));
          }
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll('[data-animate]').forEach((el) => {
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  const executiveCouncil = [
    {
      role: 'CEO Agent',
      question: 'Should we do this?',
      description: 'Strategic alignment and risk acceptance authority',
      color: 'blue',
      icon: Target,
      gradient: 'from-blue-500 to-blue-600',
    },
    {
      role: 'CTO Agent',
      question: 'Can this be built safely?',
      description: 'Technical feasibility and security validation',
      color: 'green',
      icon: Code,
      gradient: 'from-green-500 to-emerald-600',
    },
    {
      role: 'CPO Agent',
      question: 'Should this exist?',
      description: 'Product value and user outcomes assessment',
      color: 'purple',
      icon: Sparkles,
      gradient: 'from-purple-500 to-violet-600',
    },
    {
      role: 'CAO Agent',
      question: 'How should this behave?',
      description: 'Autonomy levels and behavioral governance',
      color: 'gold',
      icon: Shield,
      gradient: 'from-amber-500 to-orange-600',
    },
  ];

  const features = [
    {
      icon: Shield,
      title: 'Executive Governance',
      description: 'C-suite level authority over AI behavior, not just guardrails',
      color: 'blue',
    },
    {
      icon: Layers,
      title: 'Five-Tier Autonomy',
      description: 'Nuanced control from observational to fully autonomous',
      color: 'purple',
    },
    {
      icon: RotateCcw,
      title: 'Instant Rollback',
      description: 'Pause and reverse any AI action in milliseconds',
      color: 'cyan',
    },
    {
      icon: Eye,
      title: 'Full Transparency',
      description: 'Every decision logged, explained, and auditable',
      color: 'green',
    },
    {
      icon: Lock,
      title: 'Human-in-the-Loop',
      description: 'Enforced confirmation for irreversible actions',
      color: 'amber',
    },
    {
      icon: Globe,
      title: 'Enterprise Scale',
      description: 'Built to meet SOC-2, ISO 27001, HIPAA, GDPR standards',
      color: 'rose',
    },
  ];

  const typewriterTexts = [
    'Deploy agents with confidence',
    'Govern AI behavior at scale',
    'Ensure alignment and safety',
    'Maintain human oversight',
    'Build trust in autonomy',
  ];

  return (
    <div className="relative min-h-screen">
      {/* Background Effects */}
      <AnimatedBackground variant="gaming" />
      <ParticleBackground density={40} />

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center pt-20">
        <div className="max-w-7xl mx-auto px-6 py-20">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Hero Content */}
            <div className="text-center lg:text-left">
              {/* Badge */}
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-500/10 border border-blue-500/20 mb-8 animate-fade-in">
                <Sparkles className="w-4 h-4 text-blue-400" />
                <span className="text-sm text-blue-400 font-medium">Introducing CAO Platform™ v1.0</span>
              </div>

              {/* Main Title */}
              <h1 className="text-5xl lg:text-7xl font-bold mb-6 leading-tight">
                <span className="text-white">The Chief</span>
                <br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-purple-400 to-cyan-400 animate-gradient bg-[length:200%_auto]">
                  Agentic Officer™
                </span>
              </h1>

              {/* Subtitle with Typewriter */}
              <div className="text-xl lg:text-2xl text-slate-300 mb-4 h-8">
                <TypewriterText
                  texts={typewriterTexts}
                  typingSpeed={80}
                  pauseTime={2500}
                  className="text-transparent bg-clip-text bg-gradient-to-r from-slate-200 to-slate-400"
                />
              </div>

              <p className="text-lg text-slate-400 mb-8 max-w-xl">
                Executive-level governance for autonomous AI systems. Ensure AI behaves safely,
                explainably, and in alignment with human intent.
              </p>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <Link
                  to="/demo"
                  className="group relative px-8 py-4 rounded-xl font-semibold overflow-hidden"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-purple-600" />
                  <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-purple-500 opacity-0 group-hover:opacity-100 transition-opacity" />
                  <div className="absolute inset-0 opacity-0 group-hover:opacity-100">
                    <div className="absolute inset-0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700 bg-gradient-to-r from-transparent via-white/20 to-transparent" />
                  </div>
                  <span className="relative flex items-center justify-center gap-2 text-white">
                    <Play className="w-5 h-5" />
                    Watch Demo
                  </span>
                </Link>

                <Link
                  to="/contact"
                  className="group px-8 py-4 rounded-xl font-semibold border border-slate-700 hover:border-blue-500/50 bg-slate-900/50 hover:bg-blue-500/10 transition-all duration-300"
                >
                  <span className="flex items-center justify-center gap-2 text-slate-300 group-hover:text-white">
                    Get Started
                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </span>
                </Link>
              </div>

              {/* Trust Badges */}
              <div className="mt-12 flex flex-wrap gap-6 justify-center lg:justify-start text-sm text-slate-500">
                <div className="flex items-center gap-2">
                  <Shield className="w-4 h-4 text-blue-400" />
                  <span>Compliance Ready</span>
                </div>
                <div className="flex items-center gap-2">
                  <Lock className="w-4 h-4 text-blue-400" />
                  <span>Enterprise Ready</span>
                </div>
                <div className="flex items-center gap-2">
                  <Zap className="w-4 h-4 text-amber-400" />
                  <span>99.9% Uptime</span>
                </div>
              </div>
            </div>

            {/* Hero Visual - Interactive 3D-like Element */}
            <div className="relative hidden lg:block">
              <div className="relative w-full aspect-square max-w-lg mx-auto">
                {/* Outer rings */}
                <div className="absolute inset-0 rounded-full border border-blue-500/20 animate-spin-slow" />
                <div className="absolute inset-8 rounded-full border border-purple-500/20 animate-spin-slow" style={{ animationDirection: 'reverse', animationDuration: '15s' }} />
                <div className="absolute inset-16 rounded-full border border-cyan-500/20 animate-spin-slow" style={{ animationDuration: '20s' }} />

                {/* Orbiting elements */}
                <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center animate-float shadow-lg shadow-blue-500/30">
                    <Target className="w-6 h-6 text-white" />
                  </div>
                </div>
                <div className="absolute top-1/2 right-0 translate-x-1/2 -translate-y-1/2">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center animate-float float-2 shadow-lg shadow-green-500/30">
                    <Code className="w-6 h-6 text-white" />
                  </div>
                </div>
                <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 to-violet-600 flex items-center justify-center animate-float float-3 shadow-lg shadow-purple-500/30">
                    <Sparkles className="w-6 h-6 text-white" />
                  </div>
                </div>
                <div className="absolute top-1/2 left-0 -translate-x-1/2 -translate-y-1/2">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center animate-float float-4 shadow-lg shadow-amber-500/30">
                    <Shield className="w-6 h-6 text-white" />
                  </div>
                </div>

                {/* Center element */}
                <div className="absolute inset-24 rounded-full bg-gradient-to-br from-slate-800 to-slate-900 border border-slate-700 flex items-center justify-center">
                  <div className="relative">
                    <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full blur-xl opacity-50" />
                    <div className="relative w-28 h-28 rounded-full flex items-center justify-center overflow-hidden">
                      <img src="/CAO-Icon.svg" alt="CAO Platform" className="w-full h-full object-contain" />
                    </div>
                  </div>
                </div>

                {/* Pulse rings */}
                <div className="absolute inset-24 rounded-full border-2 border-blue-500/30 animate-ping" style={{ animationDuration: '3s' }} />
              </div>
            </div>
          </div>

          {/* Scroll indicator */}
          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
            <ChevronDown className="w-6 h-6 text-slate-500" />
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="relative py-20 border-y border-slate-800/50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { label: 'Platform Uptime', value: `${stats.uptime}%`, suffix: 'SLA', icon: Activity, color: 'green' },
              { label: 'Decision Latency', value: `<${stats.latency}`, suffix: 'ms p95', icon: Clock, color: 'blue' },
              { label: 'Decisions Governed', value: stats.decisions.toLocaleString(), suffix: 'daily', icon: TrendingUp, color: 'purple' },
            ].map((stat, i) => (
              <div key={i} className="text-center">
                <div className={`inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-${stat.color}-500/10 border border-${stat.color}-500/20 mb-4`}>
                  <stat.icon className={`w-8 h-8 text-${stat.color}-400`} />
                </div>
                <div className="text-4xl lg:text-5xl font-bold text-white mb-2">
                  {stat.value}
                  <span className="text-lg text-slate-500 ml-2">{stat.suffix}</span>
                </div>
                <div className="text-slate-400">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Interactive Demo Section */}
      <section id="demo" data-animate className="relative py-24">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-5xl font-bold mb-6">
              <span className="text-white">See Governance</span>{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-400">
                in Action
              </span>
            </h2>
            <p className="text-xl text-slate-400 max-w-2xl mx-auto">
              Watch how the CAO Platform governs a production deployment request through the Executive Council.
            </p>
          </div>

          <InteractiveDemo />
        </div>
      </section>

      {/* Nellie Ross Section */}
      <section id="nellie" data-animate className="relative py-24 bg-slate-900/30">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Nellie Visual */}
            <div className="relative order-2 lg:order-1">
              <GlowingCard glowColor="purple" className="p-8">
                <div className="flex items-center gap-6 mb-8">
                  <div className="relative">
                    <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full blur-lg opacity-50" />
                    <div className="relative w-20 h-20 rounded-full flex items-center justify-center overflow-hidden">
                      <img src="/CAO-Logov2.svg" alt="Nellie Ross - CAO" className="w-full h-full object-contain" />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-white">Nellie Ross™</h3>
                    <p className="text-purple-400">Chief Agentic Officer™</p>
                  </div>
                </div>

                <blockquote className="text-lg text-slate-300 italic mb-8 pl-4 border-l-2 border-purple-500">
                  "I exist to ensure that intelligence remains aligned, power remains accountable,
                  and autonomy remains worthy of trust."
                </blockquote>

                <div className="flex flex-wrap gap-3">
                  {['Alignment', 'Safety', 'Trust', 'Reversibility'].map((value) => (
                    <span
                      key={value}
                      className="px-4 py-2 rounded-full bg-purple-500/10 border border-purple-500/20 text-purple-300 text-sm font-medium"
                    >
                      {value}
                    </span>
                  ))}
                </div>
              </GlowingCard>

              {/* Floating badges */}
              <div className="absolute -top-4 -right-4 px-3 py-1.5 rounded-lg bg-green-500/20 border border-green-500/30 text-green-400 text-xs font-medium animate-float">
                Claude Opus 4.5
              </div>
              <div className="absolute -bottom-4 -left-4 px-3 py-1.5 rounded-lg bg-blue-500/20 border border-blue-500/30 text-blue-400 text-xs font-medium animate-float float-2">
                Always Aligned
              </div>
            </div>

            {/* Nellie Content */}
            <div className="order-1 lg:order-2">
              <h2 className="text-3xl lg:text-5xl font-bold mb-6">
                <span className="text-white">Meet Your</span>{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-400">
                  Digital Executive
                </span>
              </h2>

              <p className="text-lg text-slate-400 mb-8">
                Nellie Ross™ is your Chief Agentic Officer™ — a digital human executive who governs
                how AI systems behave once deployed. She ensures every autonomous action aligns
                with safety policies and human intent.
              </p>

              <div className="space-y-6">
                {[
                  {
                    icon: Eye,
                    title: 'Behavioral Authority',
                    description: 'Reviews every autonomous action for safety and alignment',
                    color: 'blue',
                  },
                  {
                    icon: Lock,
                    title: 'Human-in-the-Loop',
                    description: 'Enforces confirmation for irreversible actions',
                    color: 'green',
                  },
                  {
                    icon: RotateCcw,
                    title: 'Pause & Rollback',
                    description: 'Can halt agents and reverse actions instantly',
                    color: 'purple',
                  },
                ].map((feature, i) => (
                  <div key={i} className="flex items-start gap-4 group">
                    <div className={`w-12 h-12 rounded-xl bg-${feature.color}-500/10 border border-${feature.color}-500/20 flex items-center justify-center group-hover:scale-110 transition-transform`}>
                      <feature.icon className={`w-6 h-6 text-${feature.color}-400`} />
                    </div>
                    <div>
                      <h4 className="font-semibold text-white mb-1">{feature.title}</h4>
                      <p className="text-slate-400 text-sm">{feature.description}</p>
                    </div>
                  </div>
                ))}
              </div>

              <Link
                to="/about#nellie"
                className="inline-flex items-center gap-2 mt-8 text-purple-400 hover:text-purple-300 font-medium transition-colors"
              >
                Learn more about Nellie
                <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Executive Council Section */}
      <section id="council" data-animate className="relative py-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-5xl font-bold mb-6">
              <span className="text-white">The Executive</span>{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-purple-400 to-amber-400">
                Council™
              </span>
            </h2>
            <p className="text-xl text-slate-400 max-w-2xl mx-auto">
              Distributed authority across specialized AI executives. No single agent can
              unilaterally deploy autonomy.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {executiveCouncil.map((agent, i) => (
              <GlowingCard key={i} glowColor={agent.color} className="h-full">
                <div className="p-6">
                  <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${agent.gradient} flex items-center justify-center mb-6 shadow-lg`}>
                    <agent.icon className="w-7 h-7 text-white" />
                  </div>

                  <h3 className="text-xl font-bold text-white mb-2">{agent.role}</h3>
                  <p className="text-lg text-slate-300 italic mb-4">"{agent.question}"</p>
                  <p className="text-sm text-slate-500">{agent.description}</p>
                </div>
              </GlowingCard>
            ))}
          </div>
        </div>
      </section>

      {/* Features Grid Section */}
      <section id="features" data-animate className="relative py-24 bg-slate-900/30">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-5xl font-bold mb-6">
              <span className="text-white">Enterprise-Grade</span>{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-400">
                Capabilities
              </span>
            </h2>
            <p className="text-xl text-slate-400 max-w-2xl mx-auto">
              Built for scale, security, and compliance from day one.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, i) => (
              <GlowingCard key={i} glowColor={feature.color}>
                <div className="p-6">
                  <div className={`w-12 h-12 rounded-xl bg-${feature.color}-500/10 border border-${feature.color}-500/20 flex items-center justify-center mb-4`}>
                    <feature.icon className={`w-6 h-6 text-${feature.color}-400`} />
                  </div>
                  <h3 className="text-lg font-semibold text-white mb-2">{feature.title}</h3>
                  <p className="text-slate-400 text-sm">{feature.description}</p>
                </div>
              </GlowingCard>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/features"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-slate-800 hover:bg-slate-700 border border-slate-700 hover:border-blue-500/50 text-white font-medium transition-all"
            >
              Explore All Features
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Five-Tier Autonomy Section */}
      <section id="tiers" data-animate className="relative py-24">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-5xl font-bold mb-6">
              <span className="text-white">Five-Tier</span>{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-cyan-400">
                Autonomy Model™
              </span>
            </h2>
            <p className="text-xl text-slate-400 max-w-2xl mx-auto">
              Nuanced control over agent independence — not binary on/off, but graduated trust.
            </p>
          </div>

          <div className="space-y-4">
            {[
              { tier: 5, name: 'Autonomous', desc: 'Agent decides & executes', percentage: 100, color: 'from-purple-500 to-violet-600' },
              { tier: 4, name: 'Supervised', desc: 'Agent decides, human approves', percentage: 80, color: 'from-blue-500 to-indigo-600' },
              { tier: 3, name: 'Collaborative', desc: 'Agent proposes, human decides', percentage: 60, color: 'from-cyan-500 to-blue-600' },
              { tier: 2, name: 'Assistive', desc: 'Agent drafts, human reviews', percentage: 40, color: 'from-green-500 to-emerald-600' },
              { tier: 1, name: 'Observational', desc: 'Agent monitors, human acts', percentage: 20, color: 'from-slate-500 to-slate-600' },
            ].map((tier) => (
              <GlowingCard key={tier.tier} glowColor="blue" hoverEffect={false}>
                <div className="p-4 flex items-center gap-6">
                  <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${tier.color} flex items-center justify-center text-white font-bold shrink-0`}>
                    T{tier.tier}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-semibold text-white">{tier.name}</h4>
                      <span className="text-sm text-slate-400">{tier.desc}</span>
                    </div>
                    <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
                      <div
                        className={`h-full bg-gradient-to-r ${tier.color} rounded-full transition-all duration-1000`}
                        style={{ width: `${tier.percentage}%` }}
                      />
                    </div>
                  </div>
                </div>
              </GlowingCard>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-24">
        <div className="max-w-4xl mx-auto px-6">
          <GlowingCard glowColor="blue" className="overflow-hidden">
            <div className="relative p-12 text-center">
              {/* Background gradient */}
              <div className="absolute inset-0 bg-gradient-to-r from-blue-600/20 via-purple-600/20 to-cyan-600/20" />

              <div className="relative">
                <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
                  Ready to Govern AI Responsibly?
                </h2>
                <p className="text-lg text-slate-300 mb-8 max-w-2xl mx-auto">
                  Join leading enterprises using the CAO Platform to deploy autonomous AI with confidence.
                </p>

                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Link
                    to="/contact"
                    className="group relative px-8 py-4 rounded-xl font-semibold overflow-hidden"
                  >
                    <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-purple-500" />
                    <div className="absolute inset-0 bg-gradient-to-r from-blue-400 to-purple-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                    <span className="relative text-white">Request Demo</span>
                  </Link>

                  <Link
                    to="/docs"
                    className="px-8 py-4 rounded-xl font-semibold border border-slate-600 hover:border-blue-500/50 bg-slate-900/50 hover:bg-blue-500/10 text-slate-300 hover:text-white transition-all"
                  >
                    View Documentation
                  </Link>
                </div>

                {/* Trust indicators */}
                <div className="mt-12 flex flex-wrap justify-center gap-8 text-sm text-slate-500">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-400" />
                    <span>Free 14-day trial</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-400" />
                    <span>No credit card required</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-400" />
                    <span>Enterprise support</span>
                  </div>
                </div>
              </div>
            </div>
          </GlowingCard>
        </div>
      </section>
    </div>
  );
}
